import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useSwitchPowerConsumerMutation } from "../../../../redux/api/controller/controllerAPI";
import {
  useLazyListPowerConsumersQuery,
  useRemoveDeviceMutation,
} from "../../../../redux/api/device/deviceAPI";
import { useLazyProfileQuery } from "../../../../redux/api/user/userAPI";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import {
  selectFilter,
  setSmartControllerIdOfOpenedViewDeviceModal,
} from "../../../../redux/features/filter/filter-slice";
import { selectProfile } from "../../../../redux/features/user/user-slice";
import DeviceListCard from "../../../../shared/components/device-list-card/device-list-card";
import {
  IListPowerConsumersGroupedResponseDTO,
  IListPowerConsumersResponseDTO,
} from "../../../../shared/oversight-core/dtos/response-dtos/list-power-consumers-response-dto";
import SwitchPowerConsumerPowerStateByIdResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/switch-power-consumer-power-state-by-id-response-dto";
import { EDeviceStatus } from "../../../../shared/oversight-core/enums/device-status";
import { EDeviceTypes } from "../../../../shared/oversight-core/enums/device-types";
import { OvstErrorCode } from "../../../../shared/oversight-core/enums/ovst-error-codes";
import { IGroupedPowerConsumers } from "../../../../shared/oversight-core/interfaces/grouped-power-consumer";
import { ISpaceClusterPowerConsumerViews } from "../../../../shared/oversight-core/interfaces/space-cluster-power-consumer-views";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import findIcon from "../../../../shared/oversight-core/utils/findIcon";
import { showSwitchErrorMessage } from "../../../../shared/oversight-core/utils/switch-error-message";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../../../../shared/oversight-core/utils/toast";
import AddUpdateSpaceModal from "../../../dashboard/space-clusters/components/models/add-update-space-modal/add-update-space-modal";
import SpaceCluster from "../../../dashboard/space-clusters/components/space-cluster/space-cluster";
import styles from "./spaces.module.scss";

interface IProps {
  isSpaceClusterAvailable: boolean;
  estimationId: string;
  setIsDevicesAvailable: React.Dispatch<React.SetStateAction<boolean>>;
}

const Spaces = (props: IProps) => {
  const { isSpaceClusterAvailable, estimationId, setIsDevicesAvailable } =
    props;

  const profile = useSelector(selectProfile);
  const auth = useSelector(selectAuth);
  const filtersStore = useSelector(selectFilter);
  const dispatch = useDispatch();

  const [showAddUpdateSpaceModal, setShowAddUpdateSpaceModal] = useState(false);
  const [deviceListGrouped, setDeviceListGrouped] = useState<
    IGroupedPowerConsumers[]
  >([]);
  const [groupedCollapsed, setGroupedCollapsed] = useState<boolean[]>([]);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [deviceListNotGrouped, setDeviceListNotGrouped] = useState<
    ISpaceClusterPowerConsumerViews[]
  >([]);
  const [isPowerConsumerUpdated, setIsPowerConsumerUpdated] = useState(false);
  const [showSpaces, setShowSpaces] = useState(false);

  const [getProfile, { isFetching: isFetchingProfile }] = useLazyProfileQuery();
  const [
    triggerListPowerConsumers,
    { isFetching: isFetchingListPowerConsumers },
  ] = useLazyListPowerConsumersQuery();
  const [removeDevice, { isLoading: isLoadingRemoveDevice }] =
    useRemoveDeviceMutation();
  const [
    switchPowerConsumerState,
    { isLoading: isLoadingSwitchPowerConsumerState },
  ] = useSwitchPowerConsumerMutation();

  useEffect(() => {
    if (
      !filtersStore.selectedSpace.clusterId ||
      filtersStore.selectedGroupBy === "Type" ||
      filtersStore.selectedDeviceType ||
      (filtersStore.selectedSpace.clusterId && filtersStore.searchText)
    ) {
      setShowSpaces(false);
      return;
    }

    if (filtersStore.selectedSpace.id) {
      setShowSpaces(true);
    }
  }, [filtersStore]);

  const updateCurrentState = async () => {
    if (profile && profile.serviceProvider === null) {
      await getProfile({ role: auth.userRole });
    }
  };

  // const updateFilters = (
  //   selectedSpace: ISelectedSpace,
  //   searchText: string,
  //   selectedDeviceType: string,
  //   selectedGroupBy: string,
  //   badgeValue: number,
  //   isClear: boolean
  // ) => {
  //   dispatch(
  //     setSelectedFilterSpace({
  //       id: selectedSpace.id,
  //       clusterId: selectedSpace.clusterId,
  //       name: selectedSpace.name,
  //     })
  //   );

  //   dispatch(
  //     setFilter({
  //       selectedSpace: {
  //         id: selectedSpace.id,
  //         clusterId: selectedSpace.clusterId,
  //         name: selectedSpace.name,
  //       },
  //       searchText: searchText,
  //       selectedDeviceType: selectedDeviceType,
  //       selectedGroupBy: selectedGroupBy,
  //       badgeValue: badgeValue,
  //       isClear: isClear,
  //     })
  //   );
  // };

  const deleteDevice = (
    clusterId: string,
    spaceId: string,
    deviceId: string,
    keepInProgramme: boolean,
    excludedSemiAutomatedSchedules: string[]
  ) => {
    removeDevice({
      clusterId,
      spaceId,
      deviceId,
      keepInProgramme,
      excludedSemiAutomatedSchedules,
    })
      .unwrap()
      .then(() => {
        showSuccessMessage("Device Removed Successfully");
        setIsPowerConsumerUpdated(true);
      })
      .catch(() => {
        showErrorMessage("Failed to remove device");
      });
  };

  const onViewDeviceModalClose = () => {
    dispatch(setSmartControllerIdOfOpenedViewDeviceModal(""));
  };

  const updateDeviceStatus = (
    spaceClusterId: string,
    spaceId: string,
    powerConsumerId: string,
    status: EDeviceStatus,
    isFromViewDeviceModal = false
  ) => {
    if (isFromViewDeviceModal) {
      dispatch(setSmartControllerIdOfOpenedViewDeviceModal(powerConsumerId));
    }

    switchPowerConsumerState({
      spaceClusterId,
      spaceId,
      powerConsumerId,
      switchingPowerState: status,
    })
      .unwrap()
      .then((res: SwitchPowerConsumerPowerStateByIdResponseDTO) => {
        updateCurrentState();
        setIsPowerConsumerUpdated(true);
        if (res.warningMessage !== null)
          showWarningMessage(`${res.warningMessage}`);
      })
      .catch((error) => {
        if (error.status === 412) {
          if (error.ovstErrorCode === OvstErrorCode.OVST_CONS_0030) {
            showErrorMessage(
              "Cannot switch power consumer power state due to on going power usage limitation to the given type of power consumer."
            );
          } else {
            showSwitchErrorMessage(error.ovstErrorCode as OvstErrorCode);
          }
        }
      });
  };

  useEffect(() => {
    if (filtersStore.selectedGroupBy === "Type") {
      if (
        filtersStore.selectedSpace.clusterId &&
        filtersStore.selectedSpace.id
      ) {
        triggerListPowerConsumers({
          searchField: "name",
          searchValue: filtersStore.searchText,
          groupByType: true,
          spaceClusterId: filtersStore.selectedSpace.clusterId,
          subRootSpaceId: filtersStore.selectedSpace.id,
          ascending: true,
        })
          .unwrap()
          .then((response: IListPowerConsumersGroupedResponseDTO) => {
            setDeviceListGrouped(response.deviceList.elements);

            const temp: boolean[] = [];
            response.deviceList.elements.forEach(() => {
              temp.push(false);
            });
            setGroupedCollapsed(temp);
          })
          .catch(() => {
            setDeviceListGrouped([]);
          });
      } else {
        triggerListPowerConsumers({
          searchField: "name",
          searchValue: filtersStore.searchText,
          groupByType: true,
          ascending: true,
        })
          .unwrap()
          .then((response: IListPowerConsumersGroupedResponseDTO) => {
            console.log(response.deviceList.elements, "here 2");
            setDeviceListGrouped(response.deviceList.elements);

            const temp: boolean[] = [];
            response.deviceList.elements.forEach(() => {
              temp.push(false);
            });
            setGroupedCollapsed(temp);
          })
          .catch(() => {
            setDeviceListGrouped([]);
          });
      }
    } else {
      if (
        filtersStore.selectedSpace.clusterId &&
        filtersStore.selectedSpace.id
      ) {
        setIsFirstTime(true);
        triggerListPowerConsumers({
          searchField: "name",
          searchValue: filtersStore.searchText,
          type: filtersStore.selectedDeviceType,
          pageNumber: 0,
          pageSize: 1000,
          spaceClusterId: filtersStore.selectedSpace.clusterId,
          subRootSpaceId: filtersStore.selectedSpace.id,
          ascending: true,
        })
          .unwrap()
          .then((response: IListPowerConsumersResponseDTO) => {
            setDeviceListNotGrouped(response.deviceList.elements);
          })
          .catch(() => {
            setDeviceListNotGrouped([]);
          });
      } else {
        setIsFirstTime(true);
        triggerListPowerConsumers({
          searchField: "name",
          searchValue: filtersStore.searchText,
          type: filtersStore.selectedDeviceType,
          pageNumber: 0,
          pageSize: 1000,
          ascending: true,
        })
          .unwrap()
          .then((response: IListPowerConsumersResponseDTO) => {
            setDeviceListNotGrouped(response.deviceList.elements);
          })
          .catch(() => {
            setDeviceListNotGrouped([]);
          });
      }
    }
  }, [triggerListPowerConsumers, filtersStore, isPowerConsumerUpdated]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFirstTime(false);
      if (filtersStore.selectedGroupBy === "Type") {
        if (
          filtersStore.selectedSpace.clusterId &&
          filtersStore.selectedSpace.id
        ) {
          triggerListPowerConsumers({
            searchField: "name",
            searchValue: filtersStore.searchText,
            groupByType: true,
            spaceClusterId: filtersStore.selectedSpace.clusterId,
            subRootSpaceId: filtersStore.selectedSpace.id,
            ascending: true,
          })
            .unwrap()
            .then((response: IListPowerConsumersGroupedResponseDTO) => {
              setDeviceListGrouped(response.deviceList.elements);

              const temp: boolean[] = [];
              response.deviceList.elements.forEach(() => {
                temp.push(false);
              });
              setGroupedCollapsed(temp);
            })
            .catch(() => {
              setDeviceListGrouped([]);
            });
        } else {
          triggerListPowerConsumers({
            searchField: "name",
            searchValue: filtersStore.searchText,
            groupByType: true,
            ascending: true,
          })
            .unwrap()
            .then((response: IListPowerConsumersGroupedResponseDTO) => {
              setDeviceListGrouped(response.deviceList.elements);

              const temp: boolean[] = [];
              response.deviceList.elements.forEach(() => {
                temp.push(false);
              });
              setGroupedCollapsed(temp);
            })
            .catch(() => {
              setDeviceListGrouped([]);
            });
        }
      } else {
        if (
          filtersStore.selectedSpace.clusterId &&
          filtersStore.selectedSpace.id
        ) {
          triggerListPowerConsumers({
            searchField: "name",
            searchValue: filtersStore.searchText,
            type: filtersStore.selectedDeviceType,
            pageNumber: 0,
            pageSize: 1000,
            spaceClusterId: filtersStore.selectedSpace.clusterId,
            subRootSpaceId: filtersStore.selectedSpace.id,
            ascending: true,
          })
            .unwrap()
            .then((response: IListPowerConsumersResponseDTO) => {
              setDeviceListNotGrouped(response.deviceList.elements);
            })
            .catch(() => {
              setDeviceListNotGrouped([]);
            });
        } else {
          triggerListPowerConsumers({
            searchField: "name",
            searchValue: filtersStore.searchText,
            type: filtersStore.selectedDeviceType,
            pageNumber: 0,
            pageSize: 1000,
            ascending: true,
          })
            .unwrap()
            .then((response: IListPowerConsumersResponseDTO) => {
              setDeviceListNotGrouped(response.deviceList.elements);
            })
            .catch(() => {
              setDeviceListNotGrouped([]);
            });
        }
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [triggerListPowerConsumers, filtersStore, isPowerConsumerUpdated]);

  return (
    <>
      {isSpaceClusterAvailable ? (
        <>
          {/* <div className="mb-3">
            <SpaceDeviceFilters
              updateFilters={updateFilters}
              isFetching={isFetchingListPowerConsumers && isFirstTime}
              updateCurrentState={updateCurrentState}
            />
          </div> */}
          <div className={`${showSpaces ? "d-block" : "d-none"}`}>
            <SpaceCluster setIsDevicesAvailable={setIsDevicesAvailable} />
          </div>

          <div className={`${showSpaces ? "d-none" : "d-block"}`}>
            {filtersStore.selectedGroupBy === "Type" && (
              <>
                {deviceListGrouped.length !== 0 ? (
                  <>
                    {deviceListGrouped.map(
                      (elements: IGroupedPowerConsumers, index) => {
                        return (
                          <div
                            key={index}
                            className="container-white mx-1 mt-4"
                          >
                            <Row
                              className="cursor-pointer"
                              onClick={() => {
                                setGroupedCollapsed((ps) => {
                                  const temp = [...ps];
                                  temp[index] = !temp[index];
                                  return [...temp];
                                });
                              }}
                            >
                              <Col
                                className={`p-1 ${elements.group} rounded col-auto`}
                              >
                                <MaterialIcon
                                  icon={findIcon(
                                    elements.group as EDeviceTypes
                                  )}
                                  size={19}
                                />
                              </Col>
                              <Col className="text-dark font-size-16 font-weight-500">{`${elements.group}s`}</Col>
                              <Col className="col-auto">
                                <MaterialIcon
                                  icon="expand_more"
                                  className={`cursor-pointer dArrow`}
                                  style={
                                    groupedCollapsed[index]
                                      ? { transform: "rotate(-90deg)" }
                                      : { transform: "rotate(0deg)" }
                                  }
                                />
                              </Col>
                            </Row>
                            {elements.spaceClusterPowerConsumers.map(
                              (spaceClusterPowerConsumer) => {
                                return (
                                  <Row
                                    key={
                                      spaceClusterPowerConsumer.powerConsumer.id
                                    }
                                    className={`${
                                      groupedCollapsed[index] ? "d-none" : ""
                                    } mt-2`}
                                  >
                                    <Col className="px-0">
                                      <DeviceListCard
                                        deleteDevice={deleteDevice}
                                        updateCurrentState={updateCurrentState}
                                        device={{
                                          ...spaceClusterPowerConsumer.powerConsumer,
                                          clusterId:
                                            spaceClusterPowerConsumer.spaceClusterId,
                                          spaceId:
                                            spaceClusterPowerConsumer.spaceId,
                                        }}
                                        mainSpaceClusterId={
                                          spaceClusterPowerConsumer.spaceClusterId
                                        }
                                        mainSpaceId={
                                          spaceClusterPowerConsumer.spaceId
                                        }
                                        path={spaceClusterPowerConsumer.path}
                                        updateDeviceStatus={updateDeviceStatus}
                                        setIsPowerConsumerUpdated={
                                          setIsPowerConsumerUpdated
                                        }
                                        smartDevices={spaceClusterPowerConsumer.linkedSmartDevices.map(
                                          (lsd) => {
                                            return {
                                              ...lsd,
                                              linkedPowerConsumer: lsd
                                                ? spaceClusterPowerConsumer.powerConsumer
                                                : null,
                                              clusterId:
                                                spaceClusterPowerConsumer.spaceClusterId,
                                              spaceId:
                                                spaceClusterPowerConsumer.spaceId,
                                            };
                                          }
                                        )}
                                        isDirectly={true}
                                        isUpdateDeviceStateLoading={
                                          isLoadingSwitchPowerConsumerState
                                        }
                                        powerConsumerIdOfOpenedViewDeviceModal={
                                          filtersStore.powerConsumerIdOfOpenedViewDeviceModal
                                        }
                                        onViewDeviceModalClose={
                                          onViewDeviceModalClose
                                        }
                                      />
                                    </Col>
                                  </Row>
                                );
                              }
                            )}
                          </div>
                        );
                      }
                    )}
                  </>
                ) : (
                  <>
                    {!isFetchingListPowerConsumers && (
                      <Row className="container-white mx-1 mt-4">
                        <div className="container-dash mt-4">
                          <Col className="text-center text-color-2 font-size-12">
                            There are no devices available for the selected
                            filters.
                          </Col>
                        </div>
                      </Row>
                    )}
                  </>
                )}
              </>
            )}

            {filtersStore.selectedGroupBy !== "Type" &&
              (filtersStore.selectedDeviceType || filtersStore.searchText) && (
                <Row className="container-white position-relative mx-1 mt-4">
                  {deviceListNotGrouped.length !== 0 ? (
                    <Col>
                      {filtersStore.selectedDeviceType && (
                        <Row className="align-items-center cursor-pointer">
                          <Col
                            className={`p-1 ${filtersStore.selectedDeviceType} rounded col-auto`}
                          >
                            <MaterialIcon
                              icon={findIcon(
                                filtersStore.selectedDeviceType as EDeviceTypes
                              )}
                              size={19}
                            />
                          </Col>
                          <Col className="text-dark font-size-16 font-weight-500">{`${filtersStore.selectedDeviceType}s`}</Col>
                        </Row>
                      )}
                      <Row>
                        <Col>
                          {deviceListNotGrouped.map(
                            (
                              spaceClusterPowerConsumer: ISpaceClusterPowerConsumerViews
                            ) => {
                              return (
                                <Row
                                  key={
                                    spaceClusterPowerConsumer.powerConsumer.id
                                  }
                                  className="mt-2"
                                >
                                  <Col className="px-0">
                                    <DeviceListCard
                                      deleteDevice={deleteDevice}
                                      updateCurrentState={updateCurrentState}
                                      device={{
                                        ...spaceClusterPowerConsumer.powerConsumer,
                                        clusterId:
                                          spaceClusterPowerConsumer.spaceClusterId,
                                        spaceId:
                                          spaceClusterPowerConsumer.spaceId,
                                      }}
                                      mainSpaceClusterId={
                                        spaceClusterPowerConsumer.spaceClusterId
                                      }
                                      mainSpaceId={
                                        spaceClusterPowerConsumer.spaceId
                                      }
                                      path={spaceClusterPowerConsumer.path}
                                      updateDeviceStatus={updateDeviceStatus}
                                      setIsPowerConsumerUpdated={
                                        setIsPowerConsumerUpdated
                                      }
                                      smartDevices={spaceClusterPowerConsumer.linkedSmartDevices.map(
                                        (lsd) => {
                                          return {
                                            ...lsd,
                                            linkedPowerConsumer: lsd
                                              ? spaceClusterPowerConsumer.powerConsumer
                                              : null,
                                            clusterId:
                                              spaceClusterPowerConsumer.spaceClusterId,
                                            spaceId:
                                              spaceClusterPowerConsumer.spaceId,
                                          };
                                        }
                                      )}
                                      isDirectly={true}
                                      isUpdateDeviceStateLoading={
                                        isLoadingSwitchPowerConsumerState
                                      }
                                      powerConsumerIdOfOpenedViewDeviceModal={
                                        filtersStore.powerConsumerIdOfOpenedViewDeviceModal
                                      }
                                      onViewDeviceModalClose={
                                        onViewDeviceModalClose
                                      }
                                    />
                                  </Col>
                                </Row>
                              );
                            }
                          )}
                        </Col>
                      </Row>
                    </Col>
                  ) : (
                    <>
                      {!isFetchingListPowerConsumers && (
                        <div className="container-dash mt-4">
                          <Col className="text-center text-color-2 font-size-12">
                            There are no devices available for the selected
                            filters.
                          </Col>
                        </div>
                      )}
                    </>
                  )}
                </Row>
              )}
          </div>
        </>
      ) : (
        <>
          <div
            className={`${styles.noSpaces} d-flex justify-content-center align-items-center mt-4 mx-4`}
          >
            <Row className="w-100 justify-content-center">
              <Col>
                <Row>
                  <Col className="text-center text-color-2 font-size-14">
                    There are no available spaces
                  </Col>
                </Row>
                <Row>
                  <Col
                    className="text-color-mode text-center text-decoration-underline font-size-14 cursor-pointer"
                    onClick={() => {
                      setShowAddUpdateSpaceModal(true);
                    }}
                  >
                    Add Space
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <AddUpdateSpaceModal
            show={showAddUpdateSpaceModal}
            spaceCreationType={"spaceCluster"}
            updateCurrentState={updateCurrentState}
            onClose={() => setShowAddUpdateSpaceModal(false)}
            onCancel={() => setShowAddUpdateSpaceModal(false)}
            closeAddUpdateSpaceModal={() => {
              setShowAddUpdateSpaceModal(false);
            }}
            spaceClusterId={""}
            parentSpaceId={""}
            estimationId={estimationId}
          />
        </>
      )}
      <SpinnerModal
        show={
          isFetchingProfile ||
          (isFetchingListPowerConsumers && isFirstTime) ||
          isLoadingRemoveDevice
        }
      />
    </>
  );
};

export default Spaces;
